import React from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import {
  SELF_ATTEST_KINDS,
  StepChecklist,
  StepKind,
  StepQuestion,
} from '../../../../support/campaigns';
import { JourneyMapPicker } from './steps/pickers/JourneyMapPicker';
import { AssessmentPicker } from './steps/pickers/AssessmentPicker';
import { QuestionSetEditor } from './steps/QuestionSetEditor';
import { ChecklistEditor } from './steps/ChecklistEditor';

/** The two-up vs single-column layout select, shared by group and self-attest (gate) steps. */
const LayoutField: React.FC<{ control: Control<any>; disabled?: boolean; help: string }> = ({
  control,
  disabled,
  help,
}) => (
  <Controller
    name="layout"
    control={control}
    render={({ field }) => (
      <TextField
        {...field}
        select
        label="Layout"
        size="small"
        fullWidth
        disabled={disabled}
        helperText={help}
      >
        <MenuItem value="list">Single column</MenuItem>
        <MenuItem value="grid-2">Two side by side</MenuItem>
        <MenuItem value="grid-3">Three side by side</MenuItem>
      </TextField>
    )}
  />
);

/**
 * Kind-specific sub-form for a CampaignStep, rendered conditionally on `stepKind`. A pure controlled
 * sub-form: it owns no state and simply binds the relevant fields on the parent form's `control`.
 *
 *   - journey_map    → JourneyMapPicker bound to `journey_map` (number | null)
 *   - assessment     → AssessmentPicker bound to `assessment` (number | null)
 *   - moodle_workshop→ `launch_url` + `provider` text fields
 *   - question       → QuestionSetEditor bound to `questions` (StepQuestion[] → metadata.questions)
 *   - salesforce_link→ an informational line (bound at runtime to the SE's enrollment linkage)
 *   - group          → an informational line (a group only contains sub-steps)
 *   - content / action_item / followup → nothing extra
 */
export function StepKindFields(props: {
  control: Control<any>;
  stepKind: StepKind;
  disabled?: boolean;
}): JSX.Element | null {
  const { control, stepKind, disabled } = props;

  if (stepKind === 'journey_map') {
    return (
      <Controller
        name="journey_map"
        control={control}
        render={({ field }) => (
          <JourneyMapPicker
            value={field.value ?? null}
            onChange={(id) => field.onChange(id)}
            disabled={disabled}
          />
        )}
      />
    );
  }

  if (stepKind === 'assessment') {
    return (
      <Controller
        name="assessment"
        control={control}
        render={({ field }) => (
          <AssessmentPicker
            value={field.value ?? null}
            onChange={(id) => field.onChange(id)}
            disabled={disabled}
          />
        )}
      />
    );
  }

  if (stepKind === 'moodle_workshop') {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="launch_url"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Launch URL"
              size="small"
              fullWidth
              disabled={disabled}
              helperText="External workshop link"
            />
          )}
        />
        <Controller
          name="provider"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Provider"
              size="small"
              fullWidth
              disabled={disabled}
              helperText="e.g. moodle"
            />
          )}
        />
      </Stack>
    );
  }

  if (stepKind === 'question') {
    return (
      <Controller
        name="questions"
        control={control}
        render={({ field }) => (
          <QuestionSetEditor
            value={(field.value as StepQuestion[]) ?? []}
            onChange={(questions) => field.onChange(questions)}
            disabled={disabled}
          />
        )}
      />
    );
  }

  if (stepKind === 'salesforce_link') {
    return (
      <Typography variant="body2" color="text.secondary">
        The SE picks a Salesforce account (and optionally an opportunity) in the runner; the choice is
        saved on their enrollment. When an account is already in context, the opportunity list is
        scoped to it. Nothing to configure here.
      </Typography>
    );
  }

  if (stepKind === 'group') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">
          A group contains sub-steps — add them after saving.
        </Typography>
        <LayoutField
          control={control}
          disabled={disabled}
          help="Lays this group’s sub-steps out side by side (e.g. enablement courses or journey maps two/three across) instead of stacked."
        />
      </Stack>
    );
  }

  // content / action_item / followup: author an optional self-attest checklist, and — when it has
  // items — flag it as the campaign's first-step gate and lay it out two-up (the reference training
  // gate's two enablement cards).
  if (SELF_ATTEST_KINDS.has(stepKind)) {
    return (
      <Stack spacing={1.5}>
        <Controller
          name="checklist"
          control={control}
          render={({ field }) => (
            <ChecklistEditor
              value={(field.value as StepChecklist) ?? { items: [] }}
              onChange={(checklist) => field.onChange(checklist)}
              disabled={disabled}
            />
          )}
        />

        <Controller
          name="gates_campaign"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  disabled={disabled}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Gate the campaign on this checklist
                  </Box>
                  <Box
                    component="span"
                    sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                  >
                    Until every item here is ticked, the SE can’t reach any other step — the
                    campaign’s up-front training gate. Needs at least one checklist item.
                  </Box>
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          )}
        />

        <LayoutField
          control={control}
          disabled={disabled}
          help="“Two side by side” lays the checklist items out in a two-column grid."
        />
      </Stack>
    );
  }

  return null;
}

export default StepKindFields;
